export interface ServiceListItem {
  id: string
  name: string
  serviceCode?: string
  shortDescription?: string
  providerId: string
  providerName: string
  providerBusinessName?: string
  /** ISO 4217 currency code snapshot for the provider — BE-authoritative
   *  (Provider.Currency, seeded via Domain.Common.RegionDefaults). Falls
   *  back to formatMoney's canonical 'GBP' if absent on legacy rows. */
  providerCurrency?: string
  /** Provider's home region (ISO 3166-1 alpha-2). Read-only presentation
   *  hint (e.g. address-form country default). Never used to derive currency
   *  on FE — use providerCurrency instead. */
  providerRegionCode?: string
  categoryId: string
  categoryName: string
  price: number
  durationMinutes: number
  isActive: boolean
  status: ServiceStatus
  createdAt: string
  averageRating: number
  reviewCount: number
  primaryImageUrl?: string
  logoImageUrl?: string
  tags?: ServiceTag[]
  requiresUpfrontPayment: boolean
  bookingFeeAmount: number
  // Booking policy settings (for displaying key factors on service cards)
  requiresApproval: boolean
  maxAdvanceBookingDays?: number
  minAdvanceBookingMinutes?: number
  cancellationHoursBeforeAppointment?: number
  amendmentHoursBeforeAppointment?: number
  primaryLocationAddressLine1?: string
  primaryLocationCity?: string
  primaryLocationPostalCode?: string
  // Recurring appointment configuration
  allowRecurringBookings?: boolean
  maxRecurringAppointments?: number
  // Booking statistics for trending/popularity
  totalBookingCount: number // All-time booking count
  recentBookingCount: number // Bookings in last 7 days
  isHot: boolean // True if 5+ bookings in last 7 days
}

export interface Service {
  id: string
  name: string
  serviceCode?: string
  description: string
  shortDescription?: string
  providerId: string
  providerName: string
  providerBusinessName?: string
  providerBusinessDescription?: string
  providerWebsite?: string
  /** ISO 4217 currency snapshot for the provider (Provider.Currency).
   *  BE-authoritative; falls back to formatMoney's 'GBP' if absent. */
  providerCurrency?: string
  /** Provider's home region (ISO 3166-1). Presentation hint only —
   *  do NOT derive currency from this; use providerCurrency. */
  providerRegionCode?: string
  providerAverageRating: number
  providerTotalReviews: number
  categoryId: string
  categoryName: string
  price: number
  durationMinutes: number
  isActive: boolean
  requiresApproval: boolean
  requiresAgeVerification: boolean
  requiresDocumentUpload: boolean // If true, client must upload document(s) after booking
  maxAdvanceBookingDays?: number
  minAdvanceBookingMinutes?: number
  preparationInstructions?: string
  afterCareInstructions?: string
  cancellationPolicy?: string
  status: ServiceStatus
  // visibility removed - all services are publicly readable
  createdAt: string
  updatedAt: string
  averageRating: number
  reviewCount: number
  documents: ServiceDocument[]
  locations: ServiceLocation[]
  availabilities: ServiceAvailability[]
  tags?: ServiceTag[]
  // Recurring appointment configuration
  allowRecurringBookings: boolean
  allowedRecurrenceTypes: number // RecurringBookingType flags
  maxRecurringAppointments: number
}

export interface ServiceDocument {
  id: string
  serviceId?: string
  documentType: ServiceDocumentType | string  // Can be enum value or string from API
  documentUrl: string
  title?: string
  description?: string
  altText?: string
  isPrimary: boolean
  isPublic: boolean
  sortOrder: number
  fileSizeBytes?: number
  mimeType?: string
  originalFileName?: string
  /** True when this document is currently the service's downloadable flyer. */
  isFlyer?: boolean
}

// Legacy interface for backward compatibility
export interface ServiceImage {
  id: string
  imageUrl: string
  altText?: string
  isPrimary: boolean
  sortOrder: number
}

export interface ServiceLocation {
  id: string
  locationId: string
  locationName: string
  locationAddress: string
  isDefault: boolean
}

export interface ServiceAvailability {
  id: string
  dayOfWeek: number // 0 = Sunday, 1 = Monday, etc.
  startTime: string // TimeOnly format: "HH:mm:ss"
  endTime: string // TimeOnly format: "HH:mm:ss"
  isAvailable: boolean
  effectiveFrom?: string
  effectiveTo?: string
  type: AvailabilityType
}

export interface ServiceCategory {
  id: string
  name: string
  description: string
  icon?: string
  color?: string
  parentId?: string
  parentName?: string
  isActive: boolean
  sortOrder: number
  servicesCount: number
  children: ServiceCategory[]
}

export interface ServiceReview {
  id: string
  serviceId: string
  clientId: string
  clientName: string
  rating: number
  comment?: string
  isVerified: boolean
  isVisible: boolean
  reviewedAt: string
}

export interface ServiceSearchRequest {
  searchTerm?: string
  locationSearchTerm?: string
  categoryId?: string
  categoryIds?: string[]  // Support multiple category filtering
  providerId?: string
  minPrice?: number
  maxPrice?: number
  status?: ServiceStatus
  // visibility removed - all services are publicly readable
  isActive?: boolean
  locationId?: string
  latitude?: number
  longitude?: number
  radiusKm?: number
  page: number
  pageSize: number
  sortBy: string
  sortDirection: 'asc' | 'desc'
}

export interface ServiceSearchResponse {
  services: ServiceListItem[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  categories: ServiceCategory[]
  minPrice: number
  maxPrice: number
}

export enum ServiceStatus {
  Draft = 'Draft',
  Active = 'Active',
  Inactive = 'Inactive',
  Archived = 'Archived',
  UnderReview = 'UnderReview',
  Rejected = 'Rejected'
}

export enum ServiceDocumentType {
  // Image types
  Image = 1,
  Logo = 2,
  Thumbnail = 3,
  Banner = 4,

  // Document types
  Brochure = 10,
  Menu = 11,
  Certificate = 12,
  Manual = 13,
  Contract = 14,

  // Media types
  Video = 20,
  Audio = 21,

  // Other
  Other = 99
}

// ServiceVisibility enum removed - all services are publicly readable

export enum AvailabilityType {
  Regular = 'Regular',
  Exception = 'Exception',
  Holiday = 'Holiday',
  Vacation = 'Vacation'
}

// Recurring booking type flags (can be combined with bitwise OR)
export enum RecurringBookingType {
  None = 0,
  Daily = 1,
  Weekly = 2,
  Monthly = 4,
  All = 7 // Daily | Weekly | Monthly
}

export interface ServiceTag {
  id: string
  serviceId: string
  tagName: string
  tagColor?: string
  sortOrder: number
  createdAt: string
}

// =====================
// Service-Product Linking Types (Shop Integration)
// =====================

/**
 * Variant summary for booking selection
 */
export interface ProductVariantSummary {
  id: string
  name: string
  price: number
  stockQuantity: number
  isAvailable: boolean
}

/**
 * Full link details for provider management and booking flow.
 * Includes product information and variants.
 */
export interface ServiceProductLink {
  id: string
  productId: string
  productName: string
  productTitle?: string
  productPrice: number
  /** ISO 4217 currency code of the shop selling this product (BE-authoritative). */
  productCurrency?: string
  productImageUrl?: string
  sortOrder: number
  isRequired: boolean
  showVariants: boolean
  variants: ProductVariantSummary[]
  shopBaseCategorySlugs: string[]
}

/**
 * Response with all linked products for a service (for booking flow).
 */
export interface ServiceLinkedProducts {
  serviceId: string
  requiresProductSelection: boolean
  linkedProducts: ServiceProductLink[]
}

/**
 * Product available to be linked (from provider's shop).
 */
export interface AvailableProductForLinking {
  id: string
  name: string
  title?: string
  price: number
  imageUrl?: string
  isAlreadyLinked: boolean
  variantCount: number
}

/**
 * Request to link a product to a service.
 */
export interface LinkProductToServiceRequest {
  productId: string
  sortOrder?: number
  isRequired?: boolean
  showVariants?: boolean
}

/**
 * Request to link multiple products to a service at once.
 */
export interface LinkProductsToServiceRequest {
  productIds: string[]
  isRequired?: boolean
  showVariants?: boolean
}

/**
 * Request to update link settings.
 */
export interface UpdateServiceProductLinkRequest {
  sortOrder: number
  isRequired: boolean
  showVariants: boolean
}

/**
 * Request to reorder linked products.
 */
export interface ReorderServiceProductLinksRequest {
  productIds: string[]
}

/**
 * Product selection during booking.
 */
export interface ProductSelectionDto {
  selectedProductId?: string
  selectedVariantId?: string
}